import React from 'react'
import type { MapViewProps } from './MapView'

/**
 * The part of MapView's contract that changes with the active game phase.
 * The live game screen owns the MapView itself; phase controls only register
 * this small projection of their interaction state with the shared host.
 */
export type GameMapSceneConfig = Partial<
  Pick<
    MapViewProps,
    | 'selectedTerritoryId'
    | 'secondaryTerritoryId'
    | 'validTargetIds'
    | 'previewTargetIds'
    | 'selectedFillMode'
    | 'armyOverrides'
    | 'onTerritoryPress'
    | 'onBackgroundPress'
    | 'allTerritoriesInteractive'
    | 'showRegionBonuses'
    | 'regionFocusId'
    | 'selectedRegionId'
    | 'selectedRegionColor'
    | 'adjacentTerritoryIds'
  >
> & {
  /** UI-only context consumed by the optional live tutorial strip. */
  continuousAttackEnabled?: boolean
}

export type GameMapSceneRegistration = (
  key: string,
  config: GameMapSceneConfig | null,
) => void

export interface GameMapSceneRegistrationState {
  key: string
  config: GameMapSceneConfig
}

function sameArray(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((value, index) => Object.is(value, b[index]))
}

function sameRecord(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  const aRecord = a as Record<string, unknown>
  const bRecord = b as Record<string, unknown>
  const aKeys = Object.keys(aRecord)
  const bKeys = Object.keys(bRecord)
  return aKeys.length === bKeys.length && aKeys.every((key) => Object.is(aRecord[key], bRecord[key]))
}

/**
 * Compare the phase projection by value so a phase UI can safely re-register
 * after a parent render without causing a parent/layout-effect update loop.
 * Arrays and army override records are commonly rebuilt alongside hydrated
 * state, even when their contents have not changed.
 */
export function sameGameMapSceneConfig(
  a: GameMapSceneConfig,
  b: GameMapSceneConfig,
): boolean {
  const keys = new Set<keyof GameMapSceneConfig>([
    ...(Object.keys(a) as Array<keyof GameMapSceneConfig>),
    ...(Object.keys(b) as Array<keyof GameMapSceneConfig>),
  ])

  for (const key of keys) {
    const left = a[key]
    const right = b[key]
    if (key === 'validTargetIds' || key === 'previewTargetIds' || key === 'adjacentTerritoryIds') {
      if (!sameArray(left, right)) return false
    } else if (key === 'armyOverrides') {
      if (!sameRecord(left, right)) return false
    } else if (!Object.is(left, right)) {
      return false
    }
  }
  return true
}

/**
 * Apply a phase registration without manufacturing a new state object for an
 * unchanged projection. This is intentionally pure so the elimination and
 * resignation transitions can be regression-tested without rendering RN.
 */
export function updateGameMapSceneRegistration(
  previous: GameMapSceneRegistrationState | null,
  key: string,
  config: GameMapSceneConfig | null,
): GameMapSceneRegistrationState | null {
  if (config === null) return previous?.key === key ? null : previous
  if (previous?.key === key && sameGameMapSceneConfig(previous.config, config)) return previous
  return { key, config }
}

interface UseGameMapSceneRegistrationOptions {
  key?: string
  onChange?: GameMapSceneRegistration
  config: GameMapSceneConfig
}

/**
 * Register phase-specific map state without making the phase own the board.
 * The press callback is wrapped once so a changing phase closure does not
 * cause the shared MapView to receive a new handler on every game-state patch.
 */
export function useGameMapSceneRegistration({
  key,
  onChange,
  config,
}: UseGameMapSceneRegistrationOptions): void {
  const {
    selectedTerritoryId,
    secondaryTerritoryId,
    validTargetIds,
    previewTargetIds,
    selectedFillMode,
    armyOverrides,
    onTerritoryPress,
    onBackgroundPress,
    allTerritoriesInteractive,
    showRegionBonuses,
    regionFocusId,
    selectedRegionId,
    selectedRegionColor,
    adjacentTerritoryIds,
    continuousAttackEnabled,
  } = config
  const pressRef = React.useRef(onTerritoryPress)
  pressRef.current = onTerritoryPress
  const hasPressHandler = onTerritoryPress !== undefined

  const stablePress = React.useCallback((territoryId: string) => {
    pressRef.current?.(territoryId)
  }, [])

  const registeredConfig = React.useMemo<GameMapSceneConfig>(
    () => ({
      selectedTerritoryId,
      secondaryTerritoryId,
      validTargetIds,
      previewTargetIds,
      selectedFillMode,
      armyOverrides,
      onTerritoryPress: hasPressHandler ? stablePress : undefined,
      onBackgroundPress,
      allTerritoriesInteractive,
      showRegionBonuses,
      regionFocusId,
      selectedRegionId,
      selectedRegionColor,
      adjacentTerritoryIds,
      continuousAttackEnabled,
    }),
    [
      selectedTerritoryId,
      secondaryTerritoryId,
      validTargetIds,
      previewTargetIds,
      selectedFillMode,
      armyOverrides,
      onBackgroundPress,
      allTerritoriesInteractive,
      showRegionBonuses,
      regionFocusId,
      selectedRegionId,
      selectedRegionColor,
      adjacentTerritoryIds,
      continuousAttackEnabled,
      hasPressHandler,
      stablePress,
    ],
  )

  React.useLayoutEffect(() => {
    if (!key || !onChange) return
    onChange(key, registeredConfig)
  }, [key, onChange, registeredConfig])

  // Keep cleanup independent from the config effect. If this lived in the
  // effect above, every rebuilt array/object would first unregister the live
  // phase and then register it again, which was the source of the render loop
  // this hook is designed to avoid. This cleanup only runs when the phase key
  // changes or the phase unmounts (including resignation).
  React.useLayoutEffect(() => {
    if (!key || !onChange) return
    return () => onChange(key, null)
  }, [key, onChange])
}
